﻿<template>
  <div class="relative">
    <!-- Trigger Button -->
    <button
      @click="showModal = true"
      class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
      <span v-if="selectedLabel" class="truncate">{{ selectedLabel }}</span>
      <span v-else class="text-gray-500 dark:text-gray-400">{{
        tt('Select device and property...', 'Izberi napravo in lastnost...')
      }}</span>
      <span v-if="!hasSelection" class="material-symbols-outlined text-gray-400">chevron_right</span>
      <span v-else class="w-5" aria-hidden="true"></span>
    </button>

    <!-- Clear selection -->
    <button
      v-if="hasSelection"
      type="button"
      @click.stop="clearSelection"
      :title="tt('Clear selection', 'Počisti izbiro')"
      class="absolute top-1/2 right-2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
      <span class="material-symbols-outlined text-base">close</span>
    </button>

    <!-- Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4"
      @click.self="showModal = false">
      <div class="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div class="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
            {{ title || tt('Select Device Property', 'Izberi lastnost naprave') }}
          </h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto p-6">
          <!-- Step 1: Device Selection (Grouped by Area/Room) -->
          <div v-if="!selectedDevice" class="space-y-4">
            <!-- Search Bar -->
            <div class="relative sticky top-0 bg-white dark:bg-gray-800 pb-4 z-10">
              <input
                v-model="searchQuery"
                type="text"
                :placeholder="tt('Search devices...', 'Iščite naprave...')"
                class="w-full px-3 py-2 pl-10 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              <span class="material-symbols-outlined absolute left-3 top-2.5 text-gray-400">search</span>
              <button
                v-if="searchQuery"
                @click="clearSearch"
                class="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <span class="material-symbols-outlined text-sm">close</span>
              </button>
            </div>

            <!-- Devices Grouped by Area/Room -->
            <div v-if="devicesByAreaRoom.length > 0" class="space-y-6">
              <div v-for="[areaId, areaData] in devicesByAreaRoom" :key="areaId" class="space-y-3">
                <!-- Area Header -->
                <div v-if="areaData.area" class="flex items-center gap-2 px-2 py-1">
                  <span class="material-symbols-outlined text-xl text-primary-600 dark:text-primary-400 flex-shrink-0">
                    home
                  </span>
                  <h3 class="text-sm font-semibold text-gray-900 dark:text-white">{{ areaData.area.areaName }}</h3>
                </div>

                <!-- Rooms in Area -->
                <div v-for="room in areaData.rooms" :key="room.id" class="space-y-2 ml-2">
                  <!-- Room Header -->
                  <div class="flex items-center gap-2 px-2 py-1">
                    <span class="material-symbols-outlined text-lg text-gray-500 dark:text-gray-400 flex-shrink-0">
                      door_front
                    </span>
                    <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ room.roomName }}</h4>
                  </div>

                  <!-- Devices in Room -->
                  <div class="space-y-2 ml-4">
                    <button
                      v-for="device in room.devices"
                      :key="device.deviceId"
                      :ref="(el) => setDeviceRef(device.deviceId, el)"
                      @click="selectDevice(device)"
                      class="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
                      <div class="flex items-center gap-3 min-w-0">
                        <span
                          class="material-symbols-outlined text-2xl leading-none text-primary-600 dark:text-primary-400 flex-shrink-0">
                          {{ getDeviceIcon(device) }}
                        </span>
                        <div class="flex-1 min-w-0">
                          <div class="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {{ device.deviceName }}
                          </div>
                          <div
                            class="flex flex-wrap items-center gap-1 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                            <span
                              class="px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 font-semibold whitespace-nowrap">
                              {{ device.deviceControl || device.deviceType || 'other' }}
                            </span>
                            <span class="text-xs bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded whitespace-nowrap">
                              {{ propCount(device) }}
                            </span>
                            <span v-if="device.enabled === false" class="text-red-600 dark:text-red-400">
                              {{ tt('Disabled', 'Onemogočeno') }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>

                <!-- Devices without Room in this Area -->
                <div v-if="areaId !== 'no-area' && devicesWithoutRoomByArea[areaId]?.length" class="space-y-2 ml-2">
                  <div class="flex items-center gap-2 px-2 py-1">
                    <span class="material-symbols-outlined text-lg text-gray-500 dark:text-gray-400 flex-shrink-0">
                      folder_off
                    </span>
                    <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {{ tt('No Room', 'Brez prostora') }}
                    </h4>
                  </div>
                  <div class="space-y-2 ml-4">
                    <button
                      v-for="device in devicesWithoutRoomByArea[areaId]"
                      :key="device.deviceId"
                      @click="selectDevice(device)"
                      class="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
                      <div class="flex items-center gap-3 min-w-0">
                        <span
                          class="material-symbols-outlined text-2xl leading-none text-primary-600 dark:text-primary-400 flex-shrink-0">
                          {{ getDeviceIcon(device) }}
                        </span>
                        <div class="flex-1 min-w-0">
                          <div class="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {{ device.deviceName }}
                          </div>
                          <div
                            class="flex flex-wrap items-center gap-1 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                            <span
                              class="px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 font-semibold whitespace-nowrap">
                              {{ device.deviceControl || device.deviceType || 'other' }}
                            </span>
                            <span class="text-xs bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded whitespace-nowrap">
                              {{ propCount(device) }}
                            </span>
                            <span v-if="device.enabled === false" class="text-red-600 dark:text-red-400">
                              {{ tt('Disabled', 'Onemogočeno') }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty Devices without Room or Area -->
            <div v-else-if="devicesWithoutRoomByArea['no-area']?.length" class="space-y-3">
              <div class="flex items-center gap-2 px-2 py-1">
                <span class="material-symbols-outlined text-lg text-gray-500 dark:text-gray-400">folder_off</span>
                <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
                  {{ tt('Unassigned', 'Nerazvrščeno') }}
                </h3>
              </div>
              <div class="space-y-2 ml-2">
                <button
                  v-for="device in devicesWithoutRoomByArea['no-area']"
                  :key="device.deviceId"
                  @click="selectDevice(device)"
                  class="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
                  <div class="flex items-center gap-3 min-w-0">
                    <span
                      class="material-symbols-outlined text-2xl leading-none text-primary-600 dark:text-primary-400 flex-shrink-0">
                      {{ getDeviceIcon(device) }}
                    </span>
                    <div class="flex-1 min-w-0">
                      <div class="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {{ device.deviceName }}
                      </div>
                      <div class="flex flex-wrap items-center gap-1 mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        <span
                          class="px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 font-semibold whitespace-nowrap">
                          {{ device.deviceControl || device.deviceType || 'other' }}
                        </span>
                        <span class="text-xs bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded whitespace-nowrap">
                          {{ propCount(device) }}
                        </span>
                        <span v-if="device.enabled === false" class="text-red-600 dark:text-red-400">
                          {{ tt('Disabled', 'Onemogočeno') }}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <!-- No Devices Message -->
            <div v-else class="text-center py-12 text-gray-500 dark:text-gray-400">
              <span class="material-symbols-outlined text-5xl block mb-2">search_off</span>
              <p class="font-medium">{{ tt('No devices found', 'Ni najdenih naprav') }}</p>
              <p class="text-sm mt-1">{{ tt('Try a different search term', 'Poskusite z drugim iskalnim nizom') }}</p>
            </div>
          </div>

          <!-- Step 2: Property Selection -->
          <div v-if="selectedDevice" class="space-y-4">
            <button
              v-if="!filterDeviceId"
              @click="selectedDevice = null"
              class="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline">
              <span class="material-symbols-outlined text-sm">arrow_back</span>
              {{ tt('Back to devices', 'Nazaj na naprave') }}
            </button>

            <!-- Selected Device Header -->
            <div class="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
              <div class="flex items-center gap-3">
                <span class="material-symbols-outlined text-2xl text-primary-600 dark:text-primary-400">
                  {{ getDeviceIcon(selectedDevice) }}
                </span>
                <div>
                  <div class="font-medium text-gray-900 dark:text-white">{{ selectedDevice.deviceName }}</div>
                  <div class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    <span
                      class="px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 inline-block">
                      {{ selectedDevice.deviceControl || selectedDevice.deviceType || 'other' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Properties List -->
            <div class="space-y-2">
              <div class="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider px-2">
                {{ tt('Select a property', 'Izberi lastnost') }}
              </div>

              <!-- Property Type Filters -->
              <div v-if="availablePropertyTypes.length > 1" class="flex flex-wrap gap-2 px-2">
                <button
                  v-for="type in availablePropertyTypes"
                  :key="type"
                  @click="togglePropertyTypeFilter(type)"
                  :class="[
                    'px-3 py-1 rounded-full text-xs font-medium transition-colors',
                    selectedPropertyTypes.has(type)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600',
                  ]">
                  {{ type }}
                </button>
              </div>

              <!-- Properties List -->
              <button
                v-for="prop in filteredProperties"
                :key="prop.id"
                @click="selectProperty(prop)"
                class="w-full p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
                <div class="flex items-start justify-between gap-3">
                  <div class="flex-1">
                    <div class="font-medium text-gray-900 dark:text-white">
                      {{ prop.name || prop.valueKey }}
                    </div>
                    <div class="flex flex-wrap items-center gap-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <span
                        class="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-1 rounded">
                        {{ getPropType(prop) }}
                      </span>
                      <span class="text-xs">{{ formatValue(prop.currentValue) }}</span>
                    </div>
                  </div>
                  <span
                    v-if="!isWritable(prop)"
                    class="text-xs text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap ml-2">
                    {{ tt('Read-only', 'Samo za branje') }}
                  </span>
                </div>
              </button>

              <!-- No Properties Message -->
              <div v-if="filteredProperties.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
                <span class="material-symbols-outlined text-4xl block mb-2">info</span>
                <p class="font-medium">
                  {{ tt('No properties match the selected filters', 'Izbranim filtrom ne ustreza nobena lastnost') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
          <button
            @click="closeModal"
            class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            {{ tt('Cancel', 'Prekliči') }}
          </button>
          <button
            v-if="canConfirm"
            @click="confirm"
            class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors">
            {{ tt('Confirm', 'Potrdi') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';

interface Device {
  id?: string;
  deviceId: string;
  deviceName: string;
  deviceType?: string;
  deviceControl?: string; // primary type field from API
  icon?: string;
  enabled?: boolean;
  integrationId?: string;
  roomId?: string;
  properties: Property[];
}

interface Room {
  id: string;
  roomName: string;
  areaId?: string;
}

interface Area {
  id: string;
  areaName: string;
}

interface Property {
  id: string;
  valueKey: string;
  name?: string;
  currentValue?: unknown;
  writable?: boolean;
  valueType?: string;
  valueId?: string;
  unit?: string | null;
  metadata?: PropertyMetadata;
  loggingType?: string | null;
  modeSettable?: boolean;
  modeCategory?: string;
}

/** What the API puts in `Property.metadata`. */
export interface PropertyMetadata {
  min_value?: number | null;
  max_value?: number | null;
  step?: number | null;
}

/** One option of an enum value's trait. */
export interface EnumOption {
  value: string | number;
  label: string;
}

/** The synthetic trait this picker builds for a picked value (`buildTraitFromProperty`). */
export interface PropertyTrait {
  trait_type: string;
  unit?: string | null;
  min_value?: number | null;
  max_value?: number | null;
  step?: number | null;
  enum_options?: EnumOption[];
}

/** What this picker hands to `update:modelValue` (and so to a consumer's v-model). */
export interface PickedDeviceValue {
  valueId: string;
  deviceId?: string;
  deviceName?: string;
  propertyName?: string;
  currentValue?: unknown;
  trait?: PropertyTrait;
  loggingType?: string | null;
}

const props = defineProps<{
  modelValue?: { valueId: string; deviceName?: string; propertyName?: string } | null;
  title?: string;
  writableOnly?: boolean; // If true, only show writable properties
  loggingOnly?: boolean; // If true, only show values that have a loggingType set
  filterDeviceId?: string; // If set, restrict picker to this device only (skip device selection step)
  /** If set, only show values of this mode category (scene/security/climate/season/lighting) */
  category?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: PickedDeviceValue | null): void;
}>();

const SEARCH_STORAGE_KEY = 'deviceValuePicker_searchQuery';

const showModal = ref(false);
const searchQuery = ref(localStorage.getItem(SEARCH_STORAGE_KEY) || '');
const devices = ref<Device[]>([]);
const rooms = ref<Room[]>([]);
const areas = ref<Area[]>([]);
const selectedDevice = ref<Device | null>(null);
const selectedProperty = ref<Property | null>(null);
const deviceListContainer = ref<HTMLElement | null>(null);
const deviceRefs = ref<Map<string, HTMLElement>>(new Map());
const selectedPropertyTypes = ref<Set<string>>(new Set()); // Filter for property types

const setDeviceRef = (deviceId: string, el: unknown) => {
  if (el) {
    deviceRefs.value.set(deviceId, el as HTMLElement);
  } else {
    deviceRefs.value.delete(deviceId);
  }
};

const filteredDevices = computed(() => {
  const query = searchQuery.value.toLowerCase();
  return devices.value.filter(
    (d) => d.deviceName.toLowerCase().includes(query) || (d.deviceType || '').toLowerCase().includes(query),
  );
});

/**
 * Group filtered devices by area and room
 */
const devicesByAreaRoom = computed(() => {
  const areaMap = new Map<
    string,
    { area: Area | null; rooms: Map<string, { room: Room | null; devices: Device[] }> }
  >();

  // Group devices by room/area
  filteredDevices.value.forEach((device) => {
    // Normalize deviceId - API may return id or deviceId
    if (!device.deviceId && device.id) {
      device.deviceId = device.id;
    }

    let areaId = 'no-area';
    let area: Area | null = null;
    let roomId = 'no-room';
    let room: Room | null = null;

    if (device.roomId) {
      roomId = device.roomId;
      room = rooms.value.find((r) => r.id === device.roomId) || null;

      const roomAreaId = room?.areaId;
      if (roomAreaId) {
        areaId = roomAreaId;
        area = areas.value.find((a) => a.id === roomAreaId) || null;
      }
    }

    // Initialize area entry
    if (!areaMap.has(areaId)) {
      areaMap.set(areaId, { area, rooms: new Map() });
    }

    const areaData = areaMap.get(areaId)!;

    // Initialize room entry
    if (!areaData.rooms.has(roomId)) {
      areaData.rooms.set(roomId, { room, devices: [] });
    }

    areaData.rooms.get(roomId)!.devices.push(device);
  });

  // Convert to sorted array structure
  return Array.from(areaMap.entries())
    .sort(([keyA, a], [keyB, b]) => {
      // No area section goes last
      if (keyA === 'no-area') return 1;
      if (keyB === 'no-area') return -1;

      // Sort by area name
      if (a.area && b.area) {
        return a.area.areaName.localeCompare(b.area.areaName);
      }
      return 0;
    })
    .map(([areaId, areaData]) => [
      areaId,
      {
        area: areaData.area,
        rooms: Array.from(areaData.rooms.entries())
          .sort(([keyA], [keyB]) => {
            // No room section goes last
            if (keyA === 'no-room') return 1;
            if (keyB === 'no-room') return -1;
            return 0;
          })
          .map(([roomId, roomData]) => ({
            id: roomId,
            roomName: roomData.room?.roomName || tt('No Room', 'Brez prostora'),
            devices: roomData.devices,
          })),
      },
    ]) as Array<[string, { area: Area | null; rooms: Array<{ id: string; roomName: string; devices: Device[] }> }]>;
});

/**
 * Devices without rooms organized by area
 */
const devicesWithoutRoomByArea = computed(() => {
  const result: Record<string, Device[]> = {};

  filteredDevices.value
    .filter((d) => !d.roomId)
    .forEach((device) => {
      const areaId = 'no-area';
      if (!result[areaId]) {
        result[areaId] = [];
      }
      result[areaId].push(device);
    });

  return result;
});

const selectedLabel = computed(() => {
  if (!props.modelValue?.valueId) return '';

  // Try to find the device and property
  for (const device of devices.value) {
    const prop = device.properties?.find((p) => p.id === props.modelValue?.valueId);
    if (prop) {
      const propName = prop.name || prop.valueKey;
      return `${device.deviceName} - ${propName}`;
    }
  }

  // Fallback to deviceName and propertyName from modelValue if available
  if (props.modelValue.deviceName && props.modelValue.propertyName) {
    return `${props.modelValue.deviceName} - ${props.modelValue.propertyName}`;
  }

  return props.modelValue.valueId;
});

const canConfirm = computed(() => {
  return !!selectedProperty.value;
});

/** A cleared picker shows the placeholder and the chevron again. */
const hasSelection = computed(() => Boolean(props.modelValue?.valueId));

/** Drop the current selection — the consumer's v-model is nulled. */
const clearSelection = () => {
  emit('update:modelValue', null);
  selectedDevice.value = null;
  selectedProperty.value = null;
  selectedPropertyTypes.value.clear();
};

/**
 * The device row's `<n> props` badge, as a pair: the count word follows the
 * reader the way `1 prop` / `3 props` already did (`lastnost` /
 * `lastnosti` — the same agreement the tab's `bindingCountLabel` uses).
 */
const propCount = (device: Device): string => {
  const n = device.properties?.length || 0;
  return `${n} ${n === 1 ? tt('prop', 'lastnost') : tt('props', 'lastnosti')}`;
};

/**
 * Get the display type for a property - uses valueType directly from the property
 */
const getPropType = (prop: Property): string => {
  return prop.valueType || 'Unknown';
};

/**
 * Get all unique value types for the currently selected device
 */
const availablePropertyTypes = computed(() => {
  if (!selectedDevice.value?.properties) return [];

  const types = new Set<string>();
  selectedDevice.value.properties.forEach((prop) => {
    const type = getPropType(prop);
    if (type && type !== 'Unknown') types.add(type);
  });

  return Array.from(types).sort();
});

/**
 * Filter properties based on selected type filters and loggingOnly mode
 */
const filteredProperties = computed(() => {
  if (!selectedDevice.value?.properties) return [];

  let props_ = selectedDevice.value.properties;

  // When loggingOnly, restrict to values that have logging configured
  if (props.loggingOnly) {
    props_ = props_.filter((prop) => prop.loggingType && prop.loggingType !== 'None');
  }

  // Restrict to a mode category when requested
  if (props.category) {
    props_ = props_.filter((prop) => prop.modeCategory === props.category);
  }

  // If no type filters selected, return all (possibly logging-filtered) properties
  if (selectedPropertyTypes.value.size === 0) {
    return props_;
  }

  return props_.filter((prop) => {
    const type = getPropType(prop);
    return selectedPropertyTypes.value.has(type);
  });
});

const fetchDevices = async () => {
  try {
    const params = new URLSearchParams();
    if (!props.writableOnly) params.set('includeReadOnly', 'true');
    if (props.category) params.set('category', props.category);
    const qs = params.toString();
    const url = qs ? `/devices/for-scenes?${qs}` : '/devices/for-scenes';
    devices.value = await apiFetch(url);
  } catch (err) {
    console.error('Failed to load devices', err);
  }
};

const fetchRooms = async () => {
  try {
    rooms.value = await apiFetch('/rooms');
  } catch (err) {
    console.error('Failed to load rooms', err);
  }
};

const fetchAreas = async () => {
  try {
    areas.value = await apiFetch('/areas');
  } catch (err) {
    console.error('Failed to load areas', err);
  }
};

const selectDevice = (device: Device) => {
  selectedDevice.value = device;
  // Reset property type filters when changing device
  selectedPropertyTypes.value.clear();
};

const selectProperty = (prop: Property) => {
  console.log('[DeviceValuePicker] selectProperty called with:', prop);
  console.log('[DeviceValuePicker] prop keys:', Object.keys(prop));

  // Check if writable only mode is enabled
  if (props.writableOnly && !isWritable(prop)) {
    return;
  }

  selectedProperty.value = prop;

  // Confirm immediately after selecting property
  confirm();
};

const togglePropertyTypeFilter = (type: string) => {
  if (selectedPropertyTypes.value.has(type)) {
    selectedPropertyTypes.value.delete(type);
  } else {
    selectedPropertyTypes.value.add(type);
  }
  // Trigger reactivity by creating a new Set
  selectedPropertyTypes.value = new Set(selectedPropertyTypes.value);
};

const isWritable = (prop: Property): boolean => {
  return prop.modeSettable !== false;
};

/**
 * Build a synthetic trait object from a device value's fields.
 * For enum types, enum_options will be populated later (async) in confirm().
 */
const buildTraitFromProperty = (prop: Property): PropertyTrait => {
  const vt = (prop.valueType || '').toLowerCase();
  const metadata: PropertyMetadata = prop.metadata || {};

  if (vt === 'boolean') {
    return { trait_type: 'Boolean' };
  }
  if (vt === 'number') {
    return {
      trait_type: 'Number',
      unit: prop.unit ?? null,
      min_value: metadata.min_value ?? null,
      max_value: metadata.max_value ?? null,
      step: metadata.step ?? null,
    };
  }
  if (vt === 'string' && (prop.valueKey === 'color' || prop.valueKey?.includes('color'))) {
    return { trait_type: 'Color' };
  }
  if (vt === 'enum') {
    return { trait_type: 'Enum', enum_options: [] };
  }
  return { trait_type: 'String' };
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/**
 * Get material icon for a device based on its icon property
 */
function getDeviceIcon(device: Device): string {
  const icon = device.icon?.toLowerCase() || '';
  const iconMap: Record<string, string> = {
    device: 'devices',
    light: 'lightbulb',
    switch: 'toggle_on',
    sensor: 'sensors',
    cover: 'blinds',
    thermostat: 'thermostat',
    camera: 'videocam',
    lock: 'lock',
    fan: 'mode_fan',
    outlet: 'power',
    video: 'camera',
  };

  if (iconMap[icon]) {
    return iconMap[icon];
  }

  return device.icon || 'devices';
}

const confirm = async () => {
  if (!selectedProperty.value) return;

  // Build synthetic trait from value fields
  const trait = buildTraitFromProperty(selectedProperty.value);

  // For enum type, fetch options from backend
  if (trait.trait_type === 'Enum') {
    try {
      const opts = await apiFetch(`/devices/values/${selectedProperty.value.id}/options`);
      trait.enum_options = opts.enum_options || [];
    } catch (err) {
      console.warn('[DeviceValuePicker] Failed to load enum options:', err);
    }
  }

  const result: PickedDeviceValue = {
    valueId: selectedProperty.value.id,
    deviceId: selectedDevice.value?.deviceId,
    deviceName: selectedDevice.value?.deviceName,
    propertyName: selectedProperty.value.name || selectedProperty.value.valueKey,
    currentValue: selectedProperty.value.currentValue,
    trait,
    loggingType: selectedProperty.value.loggingType ?? null,
  };

  console.log('[DeviceValuePicker] Emitting update:modelValue with:', result);
  emit('update:modelValue', result);
  // Close modal but keep search query for next time
  showModal.value = false;
  selectedDevice.value = null;
  selectedProperty.value = null;
  // Don't clear searchQuery here - persist it
};

const clearSearch = () => {
  searchQuery.value = '';
  localStorage.removeItem(SEARCH_STORAGE_KEY);
};

const closeModal = () => {
  showModal.value = false;
  selectedDevice.value = null;
  selectedProperty.value = null;
  selectedPropertyTypes.value.clear();
  clearSearch();
};

const scrollToExistingDevice = async (valueId: string) => {
  // Find the device that contains this value
  for (const device of devices.value) {
    const prop = device.properties?.find((p) => p.valueId === valueId);
    if (prop) {
      // Wait for the DOM to update
      await nextTick();

      // Get the device element from refs
      const deviceEl = deviceRefs.value.get(device.deviceId);
      if (deviceEl && deviceListContainer.value) {
        // Scroll the device into view
        deviceEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Add a brief highlight effect
        deviceEl.classList.add('ring-2', 'ring-blue-500');
        setTimeout(() => {
          deviceEl.classList.remove('ring-2', 'ring-blue-500');
        }, 2000);
      }
      break;
    }
  }
};

// Save search query to localStorage whenever it changes
watch(searchQuery, (newValue) => {
  if (newValue) {
    localStorage.setItem(SEARCH_STORAGE_KEY, newValue);
  } else {
    localStorage.removeItem(SEARCH_STORAGE_KEY);
  }
});

watch(showModal, async (isOpen) => {
  if (isOpen) {
    if (devices.value.length === 0) {
      await fetchDevices();
      await fetchRooms();
      await fetchAreas();
    }

    // If filterDeviceId is set, auto-select that device to skip step 1
    if (props.filterDeviceId) {
      const device = devices.value.find((d) => d.deviceId === props.filterDeviceId || d.id === props.filterDeviceId);
      if (device) {
        selectedDevice.value = device;
      }
    } else if (props.modelValue?.valueId) {
      // If editing an existing value, clear search and scroll to device
      searchQuery.value = '';
      await nextTick();
      scrollToExistingDevice(props.modelValue.valueId);
    }
  }
});

onMounted(() => {
  // Pre-load devices if we already have a value
  if (props.modelValue?.valueId) {
    fetchDevices();
    fetchRooms();
    fetchAreas();
  }
});
</script>
